import React from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import { MAX_CUSTOM_PERIOD_LENGTH } from './constants';
import { FISCAL_PERIOD_TYPE } from 'utils/fiscalPeriods';
import messages from './messages';
import { getRangePickerPresets, checkIfDateInRange } from './utils';

import { injectIntl, FormattedMessage } from 'react-intl';
import { LoadingOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import { RangePicker } from 'components/DatePicker';
import Button from 'components/forms/Button';
import Select from 'components/forms/Select';
import FiscalPeriodLabel from 'components/FiscalPeriodLabel';

const { Option } = Select;

const ContentContainer = styled.div`
    display: flex;
    align-items: flex-end;

    > div {
        margin-right: 10px;
    }
`;

const PeriodFormItem = ({
    intl,
    fieldName,
    year,
    grouping,
    periodType,
    periods,
    form,
    maxDateRange = MAX_CUSTOM_PERIOD_LENGTH,
}) => {
    const formItemProps = {
        name: fieldName,
        rules: [
            {
                required: true,
                message: <FormattedMessage {...messages.missingPeriodErrorMessage} />,
            },
        ],
    };
    if (periodType === FISCAL_PERIOD_TYPE.CUSTOM) {
        const rangePickerPresets = getRangePickerPresets(intl, year);

        const checkIfDateDisabled = (d) => {
            const [rangeStart, rangeEnd] = form.getFieldsValue().period || [];
            return !checkIfDateInRange(d, rangeStart, rangeEnd, maxDateRange);
        };

        const onOpenOrClose = (open) => {
            // Force re-selecting the whole range when opening the date picker
            if (open) form.setFieldsValue({ period: null });
        };

        const onCalendarChange = (dates) => {
            if (!isEmpty(dates)) {
                const [start, end] = dates;
                form.setFieldsValue({
                    period: [start, end],
                });
            }
        };

        return (
            <Form.Item {...formItemProps}>
                <RangePicker
                    allowClear
                    allowEmpty={[false, false]}
                    disabledDate={checkIfDateDisabled}
                    presets={rangePickerPresets}
                    onCalendarChange={onCalendarChange}
                    onOpenChange={onOpenOrClose}
                />
            </Form.Item>
        );
    }

    let content = null;
    if (periods.loading) {
        content = (
            <ContentContainer>
                <div>
                    <LoadingOutlined />
                </div>
                <div>
                    <FormattedMessage {...messages.loadingPeriodsMessage} />
                </div>
            </ContentContainer>
        );
    } else if (periods.error) {
        content = (
            <ContentContainer>
                <div>
                    <FormattedMessage {...messages.errorFetchingPeriodsMessage} />
                </div>
                <div>
                    <Button size="small" onClick={periods.retry}>
                        <FormattedMessage {...messages.retryButton} />
                    </Button>
                </div>
            </ContentContainer>
        );
    } else {
        const fiscalPeriods = (periods.data || []).filter((row) => row.grouping === grouping && row.year === year);
        if (fiscalPeriods.length === 0) {
            content = <FormattedMessage {...messages.noMatchingPeriodsMessage} />;
        } else {
            // Called for each option, with the current search term to check if the option should be displayed
            const filterPeriodOption = (inputValue, option) =>
                option.children.props.details.name.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0;

            content = (
                <Select name="predefinedPeriodSelector" showSearch filterOption={filterPeriodOption}>
                    {fiscalPeriods.map((p) => (
                        <Option key={p.id} value={p.id}>
                            <FiscalPeriodLabel
                                details={{
                                    name: p.name,
                                    year,
                                    type: FISCAL_PERIOD_TYPE.PREDEFINED,
                                }}
                                startDate={p.startDate}
                                endDate={p.endDate}
                            />
                        </Option>
                    ))}
                </Select>
            );
        }
    }

    return <Form.Item {...formItemProps}>{content}</Form.Item>;
};

export default injectIntl(PeriodFormItem);
